import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib/cn.ts'
import { IconAlert, IconCheckCircle, IconInfo } from './Icon.tsx'
import { Spinner } from './Spinner.tsx'

/** Level 1: a white surface lifted off the desk by contrast, no border. `lift` adds hover depth. */
export function Card({
	id,
	className,
	lift = false,
	children,
}: {
	id?: string
	className?: string
	lift?: boolean
	children: ReactNode
}) {
	return (
		<div
			id={id}
			className={cn(
				'rounded-card bg-sheet shadow-1',
				lift &&
					'transition-[box-shadow,translate] duration-(--dur-base) hover:-translate-y-0.5 hover:shadow-2 motion-reduce:hover:translate-y-0',
				className,
			)}
		>
			{children}
		</div>
	)
}

type AlertTone = 'error' | 'info' | 'busy' | 'ok' | 'warn'

const alertTone: Record<AlertTone, { box: string; icon: string; title: string }> = {
	error: { box: 'bg-error-soft', icon: 'text-error', title: 'text-error' },
	warn: { box: 'bg-warn-soft/60', icon: 'text-warn', title: 'text-ink' },
	ok: { box: 'bg-ok-soft', icon: 'text-ok', title: 'text-ink' },
	info: { box: 'bg-teal-50', icon: 'text-teal-700', title: 'text-ink' },
	busy: { box: 'bg-teal-50', icon: 'text-teal-700', title: 'text-ink' },
}

/** Inline message: what happened and what to do. `busy` is work in progress, not a problem. */
export function Alert({
	tone = 'error',
	title,
	children,
	action,
	className,
}: {
	tone?: AlertTone
	title: string
	children?: ReactNode
	action?: ReactNode
	className?: string
}) {
	const s = alertTone[tone]
	const Icon = tone === 'ok' ? IconCheckCircle : tone === 'info' ? IconInfo : IconAlert
	return (
		<div
			role={tone === 'error' ? 'alert' : 'status'}
			className={cn(
				'flex flex-col gap-3 rounded-btn px-4 py-3.5 sm:flex-row sm:items-center animate-fade-in',
				s.box,
				className,
			)}
		>
			<div className="flex min-w-0 flex-1 items-start gap-3">
				{tone === 'busy' ? (
					<Spinner className={cn('mt-0.5 size-5 shrink-0', s.icon)} />
				) : (
					<Icon className={cn('mt-0.5 size-5 shrink-0', s.icon)} />
				)}
				<div className="min-w-0 flex-1">
					<p className={cn('font-[560]', s.title)}>{title}</p>
					{children && <div className="mt-0.5 text-small text-ink-2">{children}</div>}
				</div>
			</div>
			{action && <div className="shrink-0 pl-8 sm:pl-0">{action}</div>}
		</div>
	)
}

/**
 * Nothing here yet: a ghost sheet (dashed outline, placeholder lines), one sentence, one action
 * that starts the pipeline. Never just "No data".
 */
export function EmptyState({
	icon,
	title,
	children,
	action,
	className,
}: {
	icon?: ReactNode
	title: string
	children?: ReactNode
	action?: ReactNode
	className?: string
}) {
	return (
		<div className={cn('flex flex-col items-center gap-5 px-6 py-10 text-center', className)}>
			<div
				aria-hidden="true"
				className="relative flex h-28 w-22 flex-col gap-2 rounded-[10px] border-[1.5px] border-dashed border-line-strong bg-sheet/60 p-3"
			>
				<span className="h-1.5 w-3/4 rounded-full bg-canvas-deep" />
				<span className="h-1.5 w-full rounded-full bg-canvas-deep" />
				<span className="h-1.5 w-2/3 rounded-full bg-canvas-deep" />
				{icon && (
					<span className="absolute -bottom-3 -right-3 grid size-9 place-items-center rounded-full bg-sheet text-teal-700 shadow-2">
						{icon}
					</span>
				)}
			</div>
			<div className="flex max-w-md flex-col gap-1.5">
				<p className="text-h3 text-ink">{title}</p>
				{children && <p className="text-body text-ink-2">{children}</p>}
			</div>
			{action}
		</div>
	)
}

/** Delayed 300ms (CSS only), so a fast answer never flashes a skeleton. */
const delayed: CSSProperties = { animation: 'fade-in 160ms var(--ease-out) 300ms both' }

/** One bar of a skeleton. Size it like the real thing it stands in for. */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
	return (
		<div
			aria-hidden="true"
			className={cn('rounded-input bg-canvas-deep animate-skeleton', className)}
			style={style}
		/>
	)
}

/** Lines of text still on their way. Occupies the final layout, so nothing jumps. */
export function SkeletonLines({ count = 3, className }: { count?: number; className?: string }) {
	return (
		<div className={cn('flex flex-col gap-3', className)} style={delayed} aria-hidden="true">
			{Array.from({ length: count }, (_, i) => `line-${i}`).map((key, i) => (
				<Skeleton
					key={key}
					className="h-3.5 rounded-full"
					style={{ width: `${92 - ((i * 17) % 40)}%` }}
				/>
			))}
		</div>
	)
}

/**
 * A page still loading: the shape of a page header and a sheet, never a spinner. `label` is
 * read to screen readers.
 */
export function Loading({ label }: { label: string }) {
	return (
		<div role="status" className="flex flex-col gap-6 py-6" style={delayed}>
			<span className="sr-only">{label}</span>
			<div className="flex flex-col gap-3" aria-hidden="true">
				<Skeleton className="h-9 w-64 max-w-[70%]" />
				<Skeleton className="h-4 w-96 max-w-[85%] rounded-full" />
			</div>
			<div className="rounded-card bg-sheet p-6 shadow-1 sm:p-8" aria-hidden="true">
				<SkeletonLines count={5} />
			</div>
		</div>
	)
}

type BadgeTone = 'neutral' | 'live' | 'draft' | 'new' | 'updated' | 'ok' | 'error'

const badgeTone: Record<BadgeTone, string> = {
	neutral: 'bg-canvas-deep text-ink-2',
	live: 'bg-teal-100 text-teal-800',
	updated: 'bg-teal-100 text-teal-800',
	draft: 'bg-amber-200 text-ink',
	new: 'bg-amber-200 text-ink',
	ok: 'bg-ok-soft text-ok',
	error: 'bg-error-soft text-error',
}

/** Micro text in a pill. New = amber wash; Updated / shared = teal wash. */
export function Badge({
	tone = 'neutral',
	id,
	className,
	children,
}: {
	tone?: BadgeTone
	id?: string
	className?: string
	children: ReactNode
}) {
	return (
		<span
			id={id}
			className={cn(
				'inline-flex h-5.5 items-center whitespace-nowrap rounded-full px-2 text-micro',
				badgeTone[tone],
				className,
			)}
		>
			{children}
		</span>
	)
}
